import React from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { SEO } from "@/data/seo";
import { NEEDS, getNeedBySlug } from "@/data/needs";
import { SERVICES } from "@/data/services";
import { Overline, PrimaryButton, SecondaryButton, Crumbs } from "@/components/Primitives";
import ServiceCard from "@/components/ServiceCard";
import RelatedContent from "@/components/RelatedContent";
import CTABlock from "@/components/CTABlock";
import { LEARN_MORE, CTA_CONTEXT } from "@/data/educational";
import LearnMoreCard from "@/components/LearnMoreCard";
import { resolvePricing } from "@/lib/pricing";

export const NeedsIndex = () => {
  useSeo(SEO.needs);
  return (
    <main data-testid="needs-page" className="bg-black">
      <section className="bg-black text-white border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-12 lg:py-20">
          <Crumbs items={[{ to: "/", label: "Home" }, { label: "Needs" }]} />
          <h1 className="mt-6 text-5xl sm:text-6xl lg:text-[80px] font-black tracking-tighter leading-[0.92]">
            Tell us the problem. <span className="k-yellow">We name the next step.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-white/70 text-lg">
            Pick the closest match. We will show a recommended sequence.
          </p>
        </div>
      </section>
      <section className="bg-black">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 gap-6">
          {NEEDS.map((n) => (
            <Link
              key={n.slug}
              to={`/needs/${n.slug}`}
              data-testid={`needs-card-${n.slug}`}
              className="group block p-7 border border-white/15 hover:k-hard-shadow-yellow"
            >
              <Overline className="mb-3">Need</Overline>
              <h2 className="text-2xl lg:text-3xl font-black tracking-tight leading-tight">{n.name}</h2>
              <p className="mt-3 text-white/70 text-sm">{n.summary}</p>
              <div className="mt-6 text-sm font-semibold group-hover:text-[#FFC600]">Open the path →</div>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW TO USE THIS PAGE */}
      <section className="bg-black border-b border-t border-white/10" data-testid="needs-how-to-use">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Overline className="mb-3">How to use this page</Overline>
            <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95]">
              Start from the problem, not the deliverable.
            </h2>
            <p className="mt-5 text-white/70 max-w-md">
              Most owners pick a service before naming the bottleneck. The Needs view exists to reverse that. Read the closest match, see the recommended sequence, then decide where to buy in.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/15">
            {[
              { k: "Read the closest match", v: "Skim the six options. Pick the one that matches the loudest problem, not the most ambitious goal." },
              { k: "Open the path", v: "Each need shows a recommended sequence of services. The sequence is the value, not the list." },
              { k: "Skip what already works", v: "Do not buy steps you have already solved. The path is a menu, not a checklist." },
              { k: "Bring questions to a call", v: "If two paths feel right, a strategy call is the fastest way to choose the first step." },
            ].map((it, idx) => (
              <div key={it.k} className="bg-black p-5 lg:p-6">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold k-yellow">0{idx + 1} · {it.k}</div>
                <p className="mt-2 text-sm text-white/80 leading-snug">{it.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEARN MORE COMING SOON */}
      <section className="bg-black border-b border-white/10" data-testid="needs-learn-more">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-8">
            <div>
              <Overline className="mb-3">Learn more before you decide</Overline>
              <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95]">Useful before the first call.</h2>
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/15">
            {LEARN_MORE.slice(0, 6).map((l) => (
              <LearnMoreCard key={l.slug} item={l} testId={`needs-learn-${l.slug}`} />
            ))}
          </ul>
        </div>
      </section>

      <CTABlock variant="default" />
    </main>
  );
};

export const NeedDetail = () => {
  const { slug } = useParams();
  const need = getNeedBySlug(slug);
  useSeo({ title: need ? `${need.name} | Kostaja` : "Need | Kostaja", description: need?.summary || "" });
  if (!need) {
    return (
      <main className="bg-black">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 py-24 text-center">
          <h1 className="text-5xl font-black tracking-tighter">Need not found.</h1>
          <div className="mt-8 flex justify-center gap-3">
            <SecondaryButton to="/needs">All needs</SecondaryButton>
            <PrimaryButton to="/contact">Contact</PrimaryButton>
          </div>
        </div>
      </main>
    );
  }
  const services = need.recommendedSequence.map((s) => SERVICES.find((x) => x.slug === s)).filter(Boolean);
  return (
    <main data-testid="need-detail" className="bg-black">
      <section className="bg-black text-white border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-12 lg:py-20">
          <Crumbs items={[{ to: "/", label: "Home" }, { to: "/needs", label: "Needs" }, { label: need.name }]} />
          <h1 className="mt-6 text-5xl sm:text-6xl lg:text-[80px] font-black tracking-tighter leading-[0.92]">{need.name}</h1>
          <p className="mt-5 max-w-2xl text-white/70 text-lg">{need.summary}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <PrimaryButton to="/contact" testId="need-contact">Tell us about it</PrimaryButton>
            <SecondaryButton dark href="https://calendly.com/kostaja-info/30min" target="_blank" testId="need-book">Book a Strategy Call</SecondaryButton>
          </div>
        </div>
      </section>
      <section className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20">
          <Overline className="mb-4">Recommended sequence</Overline>
          <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95]">A clear path, top to bottom.</h2>
          <ol className="mt-10 border-l border-white/25">
            {services.map((s, idx) => (
              <li key={s.slug} className="pl-6 pb-10 relative">
                <span className="absolute -left-[9px] top-1 inline-block w-4 h-4 k-bg-yellow border border-white/15" />
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-semibold">Step 0{idx + 1}</div>
                <Link to={`/services/${s.slug}`} className="block mt-2 text-2xl lg:text-3xl font-black tracking-tight hover:text-[#FFC600]">{s.name}</Link>
                <p className="mt-2 text-white/70 max-w-2xl">{s.outcome}</p>
                <div className="mt-3 text-sm font-semibold">{resolvePricing(s).displayLabel}</div>
              </li>
            ))}
          </ol>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 3).map((s) => <ServiceCard key={`card-${s.slug}`} service={s} />)}
          </div>
        </div>
      </section>

      {/* WHY A SEQUENCE MATTERS */}
      <section className="bg-black border-b border-white/10" data-testid="need-why-sequence">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Overline color="yellow" className="mb-3">Why the sequence matters</Overline>
            <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95]">
              The order is the saving.
            </h2>
            <p className="mt-5 text-white/75 max-w-md text-base leading-relaxed">
              Buying steps in the wrong order is the most common way good budgets get spent on the wrong fix. The recommended sequence above is the order we would buy it in if we owned the business.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/15">
            {[
              { k: "Common mistake", v: "Rebuilding the website before the offer or identity is settled. The new site amplifies the old confusion." },
              { k: "Common mistake", v: "Investing in local visibility before the website can convert the visit. Attention without a path leaks." },
              { k: "Common mistake", v: "Hiring a junior in-house marketer before the brand foundation exists. The marketer ends up doing brand work, badly." },
              { k: "Common mistake", v: "Treating a logo refresh as a brand system. Touchpoints still look like five different businesses." },
            ].map((it) => (
              <div key={it.v} className="bg-black p-5 lg:p-6">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold k-yellow">{it.k}</div>
                <p className="mt-2 text-sm text-white/85 leading-snug">{it.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT HAPPENS WHEN YOU CLICK */}
      <section className="bg-black border-b border-white/10" data-testid="need-cta-context">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <Overline className="mb-3">No mystery process</Overline>
            <h3 className="text-2xl lg:text-4xl font-black tracking-tight leading-[0.95]">What happens when you click.</h3>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/15">
            <div className="bg-black p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold k-yellow">Contact</div>
              <p className="mt-2 text-sm text-white/85 leading-snug">{CTA_CONTEXT.contact}</p>
            </div>
            <div className="bg-black p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold k-yellow">Strategy Call</div>
              <p className="mt-2 text-sm text-white/85 leading-snug">{CTA_CONTEXT.book}</p>
            </div>
          </div>
        </div>
      </section>

      {/* LEARN MORE */}
      <section className="bg-black border-b border-white/10" data-testid="need-learn-more">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-8">
            <div>
              <Overline className="mb-3">Learn more before you decide</Overline>
              <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95]">Useful before the first call.</h2>
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/15">
            {LEARN_MORE.slice(0, 6).map((l) => (
              <LearnMoreCard key={l.slug} item={l} testId={`need-learn-${l.slug}`} />
            ))}
          </ul>
        </div>
      </section>

      <RelatedContent serviceSlugs={need.recommendedSequence} heading={`More for ${need.name}`} />
      <CTABlock variant="need" />
    </main>
  );
};
